/**
 * Theme Context Provider
 *
 * Manages theme state with localStorage sync and DOM manipulation
 * - Reads from localStorage on mount for instant theme application
 * - Syncs with user settings from API
 * - Applies theme class to <body> element
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useUserSettings, useUpdateSettings } from '@/hooks/useUserSettings';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  effectiveTheme: 'light' | 'dark'; // Actual applied theme (resolves 'system')
  setTheme: (theme: Theme) => void;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Get system theme preference
 */
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/**
 * Get theme from localStorage or default to system
 */
const getStoredTheme = (): Theme => {
  if (typeof window === 'undefined') return 'system';

  const stored = localStorage.getItem('theme');
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }

  return 'system';
};

/**
 * Resolve theme to actual light/dark value
 */
const resolveTheme = (theme: Theme): 'light' | 'dark' => {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  // Initialize from localStorage for instant application
  const [theme, setThemeState] = useState<Theme>(getStoredTheme());
  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>(
    resolveTheme(getStoredTheme())
  );

  // Fetch user settings from API (automatically disabled when not authenticated)
  const { data: settings, isLoading } = useUserSettings();
  const updateSettings = useUpdateSettings();

  // Sync with API settings when loaded (adjust state during render on change)
  const [syncedSettings, setSyncedSettings] = useState(settings);
  if (settings !== syncedSettings) {
    setSyncedSettings(settings);
    if (settings && settings.theme) {
      const apiTheme = settings.theme as Theme;
      setThemeState(apiTheme);
      setEffectiveTheme(resolveTheme(apiTheme));
      localStorage.setItem('theme', apiTheme);
    }
  }

  // Apply theme to DOM
  useEffect(() => {
    const root = document.documentElement;

    // Remove all theme classes
    root.classList.remove('light', 'dark');

    // Add current theme class
    root.classList.add(effectiveTheme);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        effectiveTheme === 'dark' ? '#1f2937' : '#ffffff'
      );
    }
  }, [effectiveTheme]);

  // Listen for system theme changes when theme is 'system'
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setEffectiveTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [theme]);

  /**
   * Set theme and sync with API
   */
  const setTheme = async (newTheme: Theme) => {
    // Update local state immediately for instant feedback
    setThemeState(newTheme);
    setEffectiveTheme(resolveTheme(newTheme));
    localStorage.setItem('theme', newTheme);

    // Sync with API in background
    try {
      await updateSettings.mutateAsync({ theme: newTheme });
    } catch (error) {
      console.error('Failed to update theme in settings:', error);
      // Don't revert local state - offline-first approach
    }
  };

  const value: ThemeContextType = {
    theme,
    effectiveTheme,
    setTheme,
    isLoading,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to use theme context
 */
// eslint-disable-next-line react-refresh/only-export-components -- context hook co-located with its Provider (fast-refresh only)
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
